import type {
  ConditionConfiguration,
  MeasureDateRange,
} from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { Grouping } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { distinct, distinctBy } from "@entrilia/shared/utilities/arrayHelper.ts";
import type {
  ConditionField,
  DimensionDescriptor,
  DimensionGroup,
  DimensionsStructure,
  StatementDataColumnConfiguration,
  StatementLineConfiguration,
  StatementPeriodScope,
} from "../../../../../../../../api/types/svTypes.ts";
import { AreaItemType, ItemDataType } from "../../../../../../../../api/types/svTypes.ts";
import type { StatementAxisState, StatementLineState } from "../../../../store/types/fieldsState.types.ts";
import { dateRangeToShortString } from "../../../../utilities/dateRangeToShortString.ts";
import { isDimension } from "../../../../utilities/fieldsHelper.ts";
import { getDisplayCaption } from "../../../viewer/common/fields/field-captions/captionsHelper.ts";
import type { GetFieldCaption } from "../../../viewer/financialStatement/statementGridModel.ts";
import {
  DATA_COLUMN_LABEL,
  FORMULA_COLUMN_LABEL,
  getDataColumnLabel,
  hasSingleDataColumn,
  isDataColumn,
} from "../../../viewer/financialStatement/statementColumnLabels.ts";
import { resolveColumnLabel } from "../../../viewer/financialStatement/statementGridModel.ts";
import type { FormulaReferenceField } from "../../common/fields/custom-field/utilities/customFormulaParser.ts";
import { createColumnField } from "../../common/utilities/createFields.ts";
import { isReferenceDateField } from "../../common/utilities/isReferenceDateField.ts";
import { createStatementDataColumn } from "../../utils/configuration/financialStatementConfiguration.ts";
import { getPeriodPickableLines } from "./statementColumnPeriod.ts";
import { resolveLinePeriods } from "./statementLines.ts";
import type { LinePeriodOption } from "./statementOptions.ts";
import { getPeriodOptions, getRangeText } from "./statementOptions.ts";
import type { AddToSource, StatementFieldDragItem, StatementFieldSource } from "./statementRowsArea.ts";
import { toStatementFieldSource } from "./statementRowsArea.ts";
import { getUniqueLabel } from "./uniqueLabel.ts";

export const MAX_AXES = 2;

const FORMULA_COLUMN_HINT = "Formula";
const MIXED_PERIOD = "Mixed";
const MAX_OFFSET_MONTHS = 24;
const AXIS_LIMIT_REFUSAL = "Columns take two dimension axes at most.";
const AXIS_AMOUNT_REFUSAL = "Columns take dimensions — amounts belong to the statement lines.";
const DATA_COLUMN_AMOUNT_REFUSAL = "A data column is just the amount — drop dimensions into Columns instead.";
const OWN_DATE_BLOCKS_LINK =
  "This column already carries its own date condition — remove it first to follow the report's date.";
const OWN_DATE_BLOCKS_OWN_DATE = "This column already carries its own date condition.";
const QUARTER_OFFSET_CONFLICT = "Data column offsets must be whole quarters for this grouping.";
const YEAR_OFFSET_CONFLICT = "Data column offsets must be whole years for this grouping.";
const OFFSET_NEEDS_REPORT_DATE = "Set the report date to use this column's offset.";
const COLUMN_PERIOD_INHERIT_NOTE = "Each cell follows the balance type of its row.";
const UNTITLED_ROW = "Untitled";
const LABEL_TOKEN_HINT = "Type @ and a condition name to show its value in the label.";

export const axesAreaItemTypes: string[] = [
  AreaItemType.MEASURE,
  AreaItemType.DIMENSION,
  AreaItemType.AGGREGATION,
  AreaItemType.ACCOUNT,
];

export const axisDateGroupings: Grouping[] = [Grouping.ByYear, Grouping.ByQuarter, Grouping.ByMonth];

type OffsetColumn = Pick<StatementDataColumnConfiguration, "dateOffsetMonths">;

type DateChannelColumn = Pick<StatementDataColumnConfiguration, "customConditions" | "linkedConditions">;

type PeriodLine = Pick<StatementLineConfiguration, "guid" | "lineType" | "level" | "dateRange">;

type PeriodPickerLine = Pick<StatementLineConfiguration, "guid" | "lineType" | "level">;

type PeriodColumn = Pick<StatementDataColumnConfiguration, "dateRange" | "periodScope">;

export interface AxesContext {
  axes: Pick<StatementAxisState, "field">[];
  lines: Pick<StatementLineState, "dimension" | "field">[];
  dataColumns: OffsetColumn[];
}

export type AxisOutcome = { kind: "refuse"; message: string } | { kind: "add"; axis: StatementAxisState };

export type DateChannel = "own" | "linked" | "none";

export type DateChannelAttempt = "own" | "link";

export interface LabelTokenInsertion {
  text: string;
  cursor: number;
}

export interface OffsetOption {
  months: number;
  label: string;
}

export interface PeriodRowOption {
  guid: string;
  label: string;
  depth: number;
}

const offsetLabels = new Map<number, string>([
  [0, "None"],
  [-1, "−1 month"],
  [-3, "−1 quarter"],
  [-12, "−1 year"],
]);

const groupingMonths = new Map<Grouping, number>([
  [Grouping.ByQuarter, 3],
  [Grouping.ByYear, 12],
]);

const getGroupingMonths = (grouping: Grouping | null | undefined): number =>
  grouping ? (groupingMonths.get(grouping) ?? 1) : 1;

export const isDateAxis = (axis: Pick<StatementAxisState, "field">): boolean =>
  axis.field.meta.type === ItemDataType.Date;

const getOffsetStep = (axes: Pick<StatementAxisState, "field">[]): number =>
  Math.max(1, ...axes.map((axis) => getGroupingMonths(axis.field.config.grouping)));

export const isOffsetAllowed = (months: number, axes: Pick<StatementAxisState, "field">[]): boolean =>
  months % getOffsetStep(axes) === 0;

const getOffsetLabel = (months: number): string => offsetLabels.get(months) ?? `−${-months} months`;

export const getAllowedOffsets = (axes: Pick<StatementAxisState, "field">[]): OffsetOption[] =>
  Array.from({ length: MAX_OFFSET_MONTHS + 1 }, (_, monthsBack) => 0 - monthsBack)
    .filter((months) => isOffsetAllowed(months, axes))
    .map((months) => ({ months, label: getOffsetLabel(months) }));

const withOffsetCaption = (caption: string, column: OffsetColumn): string =>
  column.dateOffsetMonths ? `${caption} (${getOffsetLabel(column.dateOffsetMonths)})` : caption;

export const getColumnCaption = (
  column: Pick<StatementDataColumnConfiguration, "columnType" | "customLabel" | "dateOffsetMonths">,
  conditions: ConditionConfiguration[],
  getFieldCaption: GetFieldCaption
): string => {
  const label = getDataColumnLabel(column);
  const { text } = resolveColumnLabel(label, conditions, getFieldCaption);
  return text === label ? text : withOffsetCaption(text, column);
};

const getOffsets = (dataColumns: OffsetColumn[]): number[] =>
  dataColumns.flatMap((column) => (column.dateOffsetMonths ? [column.dateOffsetMonths] : []));

export const getGroupingOffsetConflict = (
  grouping: Grouping | null | undefined,
  dataColumns: OffsetColumn[]
): string | undefined => {
  const step = getGroupingMonths(grouping);
  if (getOffsets(dataColumns).every((offset) => offset % step === 0)) {
    return undefined;
  }
  return grouping === Grouping.ByYear ? YEAR_OFFSET_CONFLICT : QUARTER_OFFSET_CONFLICT;
};

const hasOwnCondition = (line: Pick<StatementLineState, "field">, dimensionName: string): boolean =>
  (line.field?.config.customConditions ?? []).some((condition) => condition.filter.dimensionName === dimensionName);

const getNewDateAxisGrouping = (dataColumns: OffsetColumn[]): Grouping =>
  getGroupingOffsetConflict(Grouping.ByQuarter, dataColumns) === undefined ? Grouping.ByQuarter : Grouping.ByMonth;

export const getDateChannel = (column: DateChannelColumn): DateChannel => {
  if (column.customConditions.length > 0) {
    return "own";
  }
  return column.linkedConditions.length > 0 ? "linked" : "none";
};

export const getDateChannelRefusal = (
  column: DateChannelColumn,
  attempt: DateChannelAttempt,
  getConditionLabel: (guid: string) => string
): string | undefined => {
  if (getDateChannel(column) === "own") {
    return attempt === "link" ? OWN_DATE_BLOCKS_LINK : OWN_DATE_BLOCKS_OWN_DATE;
  }
  const [linkedGuid] = column.linkedConditions;
  if (linkedGuid === undefined) {
    return undefined;
  }
  const label = getConditionLabel(linkedGuid);
  return attempt === "link"
    ? `This column already links “${label}”.`
    : `This column already links “${label}” — unlink it first to give the column a date of its own.`;
};

export const getAxisConditionDimensions = (dimensions: DimensionDescriptor[]): DimensionDescriptor[] =>
  dimensions.filter((dimension) => dimension.type !== ItemDataType.Date);

const getGroupDimensionNames = (groups: DimensionGroup[]): string[] =>
  groups.flatMap((group) => [...group.dimensions, ...getGroupDimensionNames(group.groups ?? [])]);

export const getStructureDimensions = (
  dimensions: DimensionDescriptor[],
  structure: DimensionsStructure
): DimensionDescriptor[] =>
  distinct(getGroupDimensionNames(structure.groups)).flatMap((name) =>
    dimensions.filter((dimension) => dimension.name === name)
  );

export const resolveFieldForAxes = (source: StatementFieldSource | AddToSource, context: AxesContext): AxisOutcome => {
  if (source.kind !== "dimension") {
    return { kind: "refuse", message: AXIS_AMOUNT_REFUSAL };
  }
  const { name, caption } = source.dimension;
  if (context.axes.some((axis) => axis.field.config.name === name)) {
    return { kind: "refuse", message: `${caption} already slices the columns.` };
  }
  if (context.lines.some((line) => line.dimension?.config.name === name)) {
    return { kind: "refuse", message: `${caption} is used by a Dimension line.` };
  }
  const dateAxis = context.axes.find((axis) => isReferenceDateField(axis.field.meta));
  if (dateAxis && isReferenceDateField(source.dimension)) {
    return {
      kind: "refuse",
      message: `${caption} can't be added — Columns already use ${dateAxis.field.meta.caption} as their date.`,
    };
  }
  if (isReferenceDateField(source.dimension) && context.lines.some((line) => hasOwnCondition(line, name))) {
    return {
      kind: "refuse",
      message: `${caption} can't be a column axis while a line has its own ${caption} condition.`,
    };
  }
  if (context.axes.length >= MAX_AXES) {
    return { kind: "refuse", message: AXIS_LIMIT_REFUSAL };
  }
  const field = createColumnField(source.dimension);
  if (!isDimension(field)) {
    return { kind: "refuse", message: AXIS_AMOUNT_REFUSAL };
  }
  const grouping = isReferenceDateField(field.meta) ? getNewDateAxisGrouping(context.dataColumns) : undefined;
  return { kind: "add", axis: { field: { ...field, config: { ...field.config, grouping } }, showTotal: false } };
};

export const resolveAxesDrop = (item: StatementFieldDragItem, context: AxesContext): AxisOutcome =>
  resolveFieldForAxes(toStatementFieldSource(item), context);

export const resolveDataColumnsDrop = (item: StatementFieldDragItem, context: AxesContext): AxisOutcome => {
  const source = toStatementFieldSource(item);
  return source.kind === "dimension"
    ? resolveFieldForAxes(source, context)
    : { kind: "refuse", message: DATA_COLUMN_AMOUNT_REFUSAL };
};

const defaultDataColumnLabelPattern = new RegExp(`^${DATA_COLUMN_LABEL}( \\d+)?$`);

const isDefaultDataColumnLabel = (customLabel: string | undefined): boolean =>
  customLabel !== undefined && defaultDataColumnLabelPattern.test(customLabel.trim());

export const removeDataColumn = (
  dataColumns: StatementDataColumnConfiguration[],
  guid: string
): StatementDataColumnConfiguration[] => {
  const removed = dataColumns.find((column) => column.guid === guid);
  const remaining = dataColumns.filter((column) => column.guid !== guid);
  if (!removed || !isDataColumn(removed) || !hasSingleDataColumn(remaining)) {
    return remaining;
  }
  return remaining.map((column) =>
    isDataColumn(column) && isDefaultDataColumnLabel(column.customLabel)
      ? { ...column, customLabel: undefined }
      : column
  );
};

const getColumnLabels = (dataColumns: StatementDataColumnConfiguration[]): string[] =>
  dataColumns.map(getDataColumnLabel);

export const createDataColumn = (
  dataColumns: StatementDataColumnConfiguration[]
): StatementDataColumnConfiguration => ({
  ...createStatementDataColumn(),
  customLabel: getUniqueLabel(DATA_COLUMN_LABEL, getColumnLabels(dataColumns)),
});

export const createFormulaColumn = (
  dataColumns: StatementDataColumnConfiguration[]
): StatementDataColumnConfiguration => ({
  ...createStatementDataColumn(),
  columnType: "Formula",
  customLabel: getUniqueLabel(FORMULA_COLUMN_LABEL, getColumnLabels(dataColumns)),
});

export const getRowsPeriod = (lines: PeriodLine[], defaultRange: MeasureDateRange): MeasureDateRange | undefined => {
  const periods = distinct(Object.values(resolveLinePeriods(lines, defaultRange)));
  return periods.length > 1 ? undefined : (periods[0] ?? defaultRange);
};

const getHintPeriod = (
  column: PeriodColumn,
  rowsPeriod: MeasureDateRange | undefined
): MeasureDateRange | undefined => {
  if (!column.dateRange) {
    return rowsPeriod;
  }
  if (column.periodScope !== "Selected") {
    return column.dateRange;
  }
  return rowsPeriod === column.dateRange ? rowsPeriod : undefined;
};

export const getDataColumnHint = (
  column: Pick<StatementDataColumnConfiguration, "columnType" | "dateRange" | "periodScope">,
  rowsPeriod: MeasureDateRange | undefined
): string => {
  if (!isDataColumn(column)) {
    return FORMULA_COLUMN_HINT;
  }
  return dateRangeToShortString(getHintPeriod(column, rowsPeriod)) || MIXED_PERIOD;
};

export const getFormulaReferableColumns = (
  dataColumns: StatementDataColumnConfiguration[],
  getLabel: (column: StatementDataColumnConfiguration) => string
): FormulaReferenceField[] =>
  dataColumns.filter(isDataColumn).map((column) => ({ guid: column.guid, caption: getLabel(column) }));

export const getUnknownTokensMessage = (
  label: string | undefined,
  conditions: ConditionConfiguration[],
  getFieldCaption: GetFieldCaption
): string | undefined => {
  const { unknownTokens } = resolveColumnLabel(label ?? "", conditions, getFieldCaption);
  return unknownTokens.length > 0
    ? `No condition named ${unknownTokens.map((token) => `@${token}`).join(", ")}`
    : undefined;
};

export const getConditionTokenName = (condition: ConditionConfiguration, getFieldCaption: GetFieldCaption): string =>
  getDisplayCaption(getFieldCaption(condition.filter.dimensionName), condition.customLabel, condition.systemLabel);

export const getLabelTokenNames = (conditions: ConditionConfiguration[], getFieldCaption: GetFieldCaption): string[] =>
  distinctBy(
    conditions.map((condition) => getConditionTokenName(condition, getFieldCaption)).filter((name) => name !== ""),
    (name) => name.toLowerCase()
  );

const getLabelToken = (tokenName: string): string => `@${tokenName}`;

export const getDateConditionLabel = (
  customLabel: string | undefined,
  previousTokenName: string | undefined,
  nextTokenName: string | undefined
): string | undefined => {
  const isUnnamed = !customLabel?.trim() || isDefaultDataColumnLabel(customLabel);
  const isNamedByPreviousCondition =
    previousTokenName !== undefined && customLabel === getLabelToken(previousTokenName);
  if (!isUnnamed && !isNamedByPreviousCondition) {
    return customLabel;
  }
  return nextTokenName === undefined ? undefined : getLabelToken(nextTokenName);
};

export const insertLabelToken = (
  text: string,
  selectionStart: number,
  selectionEnd: number,
  tokenName: string
): LabelTokenInsertion => {
  const token = getLabelToken(tokenName);
  return {
    text: text.slice(0, selectionStart) + token + text.slice(selectionEnd),
    cursor: selectionStart + token.length,
  };
};

export const getLabelPreview = (
  draft: string,
  conditions: ConditionConfiguration[],
  getFieldCaption: GetFieldCaption,
  defaultLabel: string
): string => {
  if (!draft.includes("@")) {
    return LABEL_TOKEN_HINT;
  }
  return `Shows as: ${resolveColumnLabel(draft, conditions, getFieldCaption).text.trim() || defaultLabel}`;
};

export const getColumnDateDimensions = (dimensions: DimensionDescriptor[]): DimensionDescriptor[] =>
  dimensions.filter((dimension) => dimension.type === ItemDataType.Date);

export const getLinkableColumnConditions = (conditions: ConditionField[]): ConditionField[] =>
  conditions.filter((condition) => isReferenceDateField(condition.meta));

export const getConditionLabel = (condition: ConditionField): string =>
  getDisplayCaption(condition.meta.caption, condition.config.customLabel, condition.config.systemLabel);

export const findLinkedReportDate = (
  column: Pick<StatementDataColumnConfiguration, "linkedConditions">,
  conditions: ConditionField[]
): ConditionField | undefined =>
  conditions.find(
    (condition) => column.linkedConditions.includes(condition.config.guid) && isReferenceDateField(condition.meta)
  );

export const getOffsetWarning = (
  column: Pick<StatementDataColumnConfiguration, "linkedConditions" | "dateOffsetMonths">,
  conditions: ConditionField[]
): string | undefined => {
  const reportDate = findLinkedReportDate(column, conditions);
  return column.dateOffsetMonths && reportDate?.config.filter.values.length === 0
    ? OFFSET_NEEDS_REPORT_DATE
    : undefined;
};

export const getColumnPeriodOptions = (rowsPeriod: MeasureDateRange | undefined): LinePeriodOption[] =>
  getPeriodOptions(`Inherit from row (${rowsPeriod ? getRangeText(rowsPeriod) : MIXED_PERIOD})`);

export const getColumnPeriodNote = (
  dateRange: MeasureDateRange | undefined,
  periodScope: StatementPeriodScope | undefined
): string => {
  if (!dateRange) {
    return COLUMN_PERIOD_INHERIT_NOTE;
  }
  return periodScope === "Selected"
    ? `Only the selected rows use ${getRangeText(dateRange)}; every other row keeps its own period.`
    : `Every cell in this column uses ${getRangeText(dateRange)}, whatever its row is set to.`;
};

export const getColumnPeriodChanges = (
  column: PeriodColumn,
  dateRange: MeasureDateRange | undefined
): Partial<StatementDataColumnConfiguration> =>
  dateRange && !column.dateRange && column.periodScope === undefined
    ? { dateRange, periodScope: "All", periodLineGuids: [] }
    : { dateRange };

export const getColumnPeriodScopeChanges = (
  column: Pick<StatementDataColumnConfiguration, "periodLineGuids">,
  periodScope: StatementPeriodScope
): Partial<StatementDataColumnConfiguration> =>
  periodScope === "Selected" ? { periodScope, periodLineGuids: column.periodLineGuids ?? [] } : { periodScope };

export const getPeriodRowOptions = <T extends PeriodPickerLine>(
  lines: T[],
  getLabel: (line: T) => string
): PeriodRowOption[] => {
  const pickableLines = getPeriodPickableLines(lines);
  const shallowestLevel = Math.min(...pickableLines.map((line) => line.level));
  return pickableLines.map((line) => ({
    guid: line.guid,
    label: getLabel(line) || UNTITLED_ROW,
    depth: line.level - shallowestLevel,
  }));
};
